import { useEffect, useRef, useState } from 'react';
import {
  getSearchPredictionsV1,
  LISTINGS_SEARCH_PREDICTION_GROUP,
  LOCATIONS_SEARCH_PREDICTION_GROUP,
} from 'data/search-predictions';
import type SearchPrediction from 'data/search-predictions';
import { getGooglePredictions } from 'utils/google-maps';
import { useFeaturesContext, useUserContext } from 'contexts';
import replaceFrenchAccents from 'utils/replace-french-accents';
import {
  LOCATION_SUGGESTION_DEBOUNCE_MS,
  MAX_LISTING_SUGGESTIONS,
  MAX_LOCATION_SUGGESTIONS,
} from './smart-search.constants';

const NO_SUGGESTIONS: SearchPrediction[] = [];

export interface SearchSuggestions {
  locations: SearchPrediction[];
  listings: SearchPrediction[];
}

const NO_RESULTS: SearchSuggestions = { locations: NO_SUGGESTIONS, listings: NO_SUGGESTIONS };

/** A completed lookup, tagged with the fragment it answers. */
interface LookupResult {
  fragment: string;
  suggestions: SearchSuggestions;
}

function mergeLocationPredictions(
  goPredictions: SearchPrediction[],
  googlePredictions: SearchPrediction[]
): SearchPrediction[] {
  const merged: SearchPrediction[] = [];
  const seen = new Set<string>();
  // Google first so its matches lead the list the way they do on the home page.
  for (const prediction of [...googlePredictions, ...goPredictions]) {
    if (prediction.group !== LOCATIONS_SEARCH_PREDICTION_GROUP) {
      continue;
    }
    const key = replaceFrenchAccents(prediction.description).toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    merged.push(prediction);
  }
  return merged.slice(0, MAX_LOCATION_SUGGESTIONS);
}

/**
 * Completes the place a query is naming, the way the site's search bar does.
 */
function selectListingPredictions(goPredictions: SearchPrediction[]): SearchPrediction[] {
  const listings: SearchPrediction[] = [];
  const seen = new Set<string>();
  for (const prediction of goPredictions) {
    if (prediction.group !== LISTINGS_SEARCH_PREDICTION_GROUP) {
      continue;
    }
    const key = prediction.id || prediction.description;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    listings.push(prediction);
  }
  return listings.slice(0, MAX_LISTING_SUGGESTIONS);
}

/**
 * Predictions come from two sources, merged exactly like the header search:
 * go-search (scoped to the tenant's country the same way — empty for Zoocasa
 * the site country otherwise) and Google Places
 *
 * Debounced per fragment and guarded by a request id, so a slow response for
 * "Tor" cannot land on top of the results for "Toron". A failed lookup yields
 * an empty list rather than an error state — the query still works without a
 * completion, and the parser gets a second pass at the place name on submit.
 */
export function useLocationSuggestions(fragment: string | null): SearchSuggestions {
  // Results are kept with the fragment they answer, so a completed lookup is
  // discarded the moment the fragment moves on instead of having to be cleared.
  const [result, setResult] = useState<LookupResult | null>(null);
  const requestIdRef = useRef(0);

  const { siteLocation } = useUserContext();
  const { features } = useFeaturesContext();
  const useUsListings = Boolean(features?.useUsListings);

  useEffect(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    if (!fragment) {
      return;
    }

    const country = siteLocation ?? '';

    const timeoutId = setTimeout(() => {
      const query = fragment.toLowerCase();

      const googleLookup: Promise<SearchPrediction[]> = siteLocation
        ? (async () =>
          getGooglePredictions(
            fragment,
            useUsListings,
            siteLocation
          ))().catch(() => NO_SUGGESTIONS)
        : Promise.resolve(NO_SUGGESTIONS);

      Promise.all([
        getSearchPredictionsV1({ query, country }).catch(() => null),
        googleLookup,
      ])
        .then(([goPredictions, googlePredictions]) => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          const resolvedGo = goPredictions ?? NO_SUGGESTIONS;
          const suggestions: SearchSuggestions = {
            locations: mergeLocationPredictions(
              resolvedGo,
              googlePredictions ?? NO_SUGGESTIONS
            ),
            listings: selectListingPredictions(resolvedGo),
          };
          setResult({ fragment, suggestions });
        })
        .catch(() => {
          if (requestId === requestIdRef.current) {
            setResult({ fragment, suggestions: NO_RESULTS });
          }
        });
    }, LOCATION_SUGGESTION_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [fragment, siteLocation, useUsListings]);

  return result?.fragment === fragment ? result.suggestions : NO_RESULTS;
}

export default useLocationSuggestions;
